import React, { useCallback, useMemo, useRef, useState } from "react"
import { Calendar as BigCalendar, dayjsLocalizer } from "react-big-calendar"
import dayjs from "dayjs"
import "dayjs/locale/pl"
import "dayjs/locale/en"
import "dayjs/locale/de"
import styled from "styled-components"
import CalendarStyles from "@/components/Calendar/styles/calendar"
import MockupEvents from "./events"
import { getCssSizeValue, getItemUrl, getPaddingClass, mergeArrays } from "@/utils/utils"
import { BUTTON_VARIANTS, CALENDAR_RANGE, PADDING } from "@/constants"
import { useBranch } from "@/hooks/useBranch"
import { useRouter } from "next/router"
import EventCard from "@/components/Calendar/EventCard"
import { DEFAULT_CARD } from "@/components/common/Cards"
import useEvents from "@/components/Calendar/useEvents"
import Toolbar from "@/components/Calendar/Toolbar"
import Button from "@/components/common/Buttons/Button"
import useGlobals from "@/hooks/useGlobals"
import dynamic from "next/dynamic"
import { BREAKPOINTS } from "@/styles/themeSettings"

const Wrapper = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	flex-direction: column;
	//min-height: 100svh;
	row-gap: 100px;
`

const ButtonsWrapper = styled.div`
	display: flex;
	column-gap: 10px;
	@media (min-width: ${({ theme }) => theme.breakpoints[BREAKPOINTS.DESKTOP]}px) and (orientation: landscape) {
		column-gap: ${getCssSizeValue(10)};
	}
`

function formatEvents(data) {
	return data.map((event, index) => {
		const start = new Date(event?.start)
		const end = new Date(event?.end || event?.start)
		// console.log(event);
		return {
			id: event?.id || index,
			start,
			end,
			title: event?.card?.header || "",
			allDay: event?.allDay || false,
		}
	})
}

const NO_EVENT = {
	id: "",
	allDay: false,
	address: "",
	card: DEFAULT_CARD,
	end: "",
	start: "",
	name: "",
	state: {
		id: "HWU3sinoSxyP3XvOyXbncA",
		name: "Glboal",
	},
	article: {},
}

const Modal = dynamic(
	import("@/components/common/Modal").then(
		(mod) => styled(mod.default)`
			.main {
				border-top: solid 1px ${({ theme }) => theme.colors.platinum};
			}
		`,
	),
	{ ssr: true },
)

const Buttons = ({ data, handleClose }) => {
	const hasArticle = Boolean(data?.article)
	const href = getItemUrl(data?.article)
	const { eventsReadMoreButton, modalButtonCloseText } = useGlobals()
	return (
		<ButtonsWrapper>
			{hasArticle && (
				<Button
					as="link"
					href={href}
					external={false}
					newCard={false}
					// aria-label="Przekierowanie na strone wydarzenia"
				>
					{eventsReadMoreButton}
				</Button>
			)}
			<Button variant={BUTTON_VARIANTS.QUATERNARY} onClick={handleClose}>
				{modalButtonCloseText}
			</Button>
		</ButtonsWrapper>
	)
}

const max = CALENDAR_RANGE.end.toString()
const min = CALENDAR_RANGE.start.toString()

const Calendar = ({ data = {} }) => {
	const navigationHeader = data?.navigationHeader || ""
	const paddingTopClass = getPaddingClass(PADDING.MEDIUM, "top")
	const paddingBottomClass = getPaddingClass(PADDING.MEDIUM, "bottom")
	const paddings = `${paddingTopClass} ${paddingBottomClass} px-main`
	const CalendarRef = useRef()
	const { selectedOption } = useBranch()
	const router = useRouter()
	const [date, setDate] = useState(new Date())
	const localizer = useMemo(() => {
		dayjs.locale(router.locale) // Update Day.js locale
		return dayjsLocalizer(dayjs) // Recreate localizer
	}, [router.locale])

	const [selectedEvent, setSelectedEvent] = useState(NO_EVENT)
	const [{ minTime, maxTime }] = useState(() => {
		const minTime = dayjs().startOf("day").add(7, "hours").toDate()
		const maxTime = dayjs().endOf("day").subtract(1, "hours").toDate()
		return {
			minTime,
			maxTime,
		}
	})
	const overMaxEvents = useEvents(max, date, router.locale)
	const beforeMinEvents = useEvents(date, min, router.locale)
	const initialEvents = useMemo(() => data?.initialEvents || MockupEvents, [data])
	const filteredEvents = useMemo(() => {
		let source = initialEvents
		if (!overMaxEvents.isLoading || !overMaxEvents.error) {
			source = mergeArrays(source, overMaxEvents.events, (a, b) => a.id === b.id)
		}
		if (!beforeMinEvents.isLoading || !beforeMinEvents.error) {
			source = mergeArrays(source, beforeMinEvents.events, (a, b) => a.id === b.id)
		}
		return source.filter((event) => event?.state?.handle?.toLowerCase() === selectedOption)
	}, [initialEvents, selectedOption, overMaxEvents, beforeMinEvents])

	const formattedEvents = useMemo(() => formatEvents(filteredEvents), [filteredEvents])

	const handleNavigate = useCallback((newDate) => {
		// console.log(newDate);
		setDate(newDate)
	}, [])

	const handleSelectEvent = useCallback(
		(newEvent) => {
			const event = filteredEvents.find((ev) => ev.id === newEvent.id)
			setSelectedEvent(event)
		},
		[filteredEvents],
	)

	const handleCloseModal = useCallback(() => {
		setSelectedEvent(NO_EVENT)
	}, [])

	// useEffect(() => {
	// 	logTable({ date, min, max })
	// }, [date]);

	// useEffect(() => {
	// 	const titles = formattedEvents.map(({ title, id }) => id)
	// 	// console.log(titles);
	// }, [formattedEvents]);

	// useEffect(() => {
	// 	// console.log(overMaxEvents);
	// 	logNamed({ overMaxEvents: overMaxEvents.events })
	// },[overMaxEvents])

	const components = useMemo(
		() => ({
			toolbar: (props) => <Toolbar navigationText={navigationHeader} {...props} />,
		}),
		[navigationHeader],
	)

	return (
		<>
			<Wrapper className={`${paddings}`}>
				<BigCalendar
					ref={CalendarRef}
					localizer={localizer}
					components={components}
					// events={[...formattedEvents,...MockupEvents]}
					events={formattedEvents}
					views={["month"]}
					startAccessor="start"
					endAccessor="end"
					defaultDate={date}
					onNavigate={handleNavigate}
					onSelectEvent={handleSelectEvent}
					max={maxTime}
					min={minTime}
					// toolbar={false}
				/>
			</Wrapper>
			<Modal
				isOpen={selectedEvent?.id}
				handleClose={handleCloseModal}
				extraComponent={<Buttons data={selectedEvent} handleClose={handleCloseModal} />}
			>
				<EventCard data={selectedEvent} />
			</Modal>
			<CalendarStyles />
		</>
	)
}

export default Calendar
